import React from "react";
import UseContext from "hooks/useContext";
import { formatTemplateToolsWithProvider } from "pages/missionTemplates/utils/templateTools";
import {
    buildTemplateConditions,
    formatRiskyPositionSummary,
    useRiskyPositions,
} from "pages/missionTemplates/utils/templateRiskyPositions";
import {
    getTemplateQualification,
    getTemplateTitle,
} from "pages/missionTemplates/utils/templateListing";

const Row = ({ label, value }) => (
    <div className={"flex justify-between items-start gap-4 py-4 border-b border-gray-100 last:border-b-0"}>
        <div className={"ft-sm text-gray-500 shrink-0"}>{label}</div>
        <div
            className={
                "ft-sm text-gray-900 text-right break-words whitespace-pre-line max-w-[70%] min-w-0"
            }
        >
            {value || "-"}
        </div>
    </div>
);

// Preview panel order, which differs from the wizard steps.
export const SUMMARY_ROWS = [
    { key: "name", defaultLabel: "Intitulé de la fiche" },
    { key: "activity", defaultLabel: "Secteur" },
    { key: "qualification", defaultLabel: "Qualification" },
    { key: "tools", defaultLabel: "Matériel" },
    { key: "licences", defaultLabel: "Permis" },
    { key: "languages", defaultLabel: "Langue" },
    { key: "riskyPosition", defaultLabel: "Poste à risque" },
    { key: "address", defaultLabel: "Adresse" },
    { key: "salary", defaultLabel: "Taux horaire brut" },
    { key: "contact", defaultLabel: "Responsable" },
    { key: "other", defaultLabel: "Note intérimaire" },
    { key: "otherInterim", defaultLabel: "Note agence" },
];

export const formatList = (list) =>
    Array.isArray(list) && list.length > 0 ? list.join(", ") : "";

export const formatAddress = (location) => {
    if (!location) return "";
    if (typeof location === "string") return location;
    const { street, postcode, city, department } = location;
    return [street, postcode, city, department].filter(Boolean).join(" ");
};

export const formatSalaryApi = (t) => {
    const wage =
        typeof t?.salary?.wage_per_hour === "number"
            ? t.salary.wage_per_hour
            : null;
    if (wage === null || Number.isNaN(wage)) return "";
    const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(wage);
    const suffix = t.isIndexedToMinimumWage ? " (indexé au SMIC)" : "";
    return `${formatted} € par heure${suffix}`;
};

const formatContact = (name, poste) =>
    name ? `${name}${poste ? ` - ${poste}` : ""}` : "";

/**
 * Formulaire assistant (templateData) — étape « Confirmation ».
 */
export const buildSummaryFromWizardData = (
    templateData,
    language,
    riskyPositions
) => ({
    name: templateData?.name,
    activity: templateData?.activity,
    qualification: templateData?.qualification,
    riskyPosition: formatRiskyPositionSummary(
        buildTemplateConditions(templateData),
        language,
        riskyPositions
    ),
    tools: formatTemplateToolsWithProvider(templateData?.tools, language),
    licences: formatList(templateData?.licences),
    languages: formatList(templateData?.languages),
    address: formatAddress(templateData?.location),
    salary:
        templateData?.salary !== undefined && templateData?.salary !== ""
            ? `${templateData.salary} € par heure${
                  templateData.isIndexedToMinimumWage
                      ? " (indexé au SMIC)"
                      : ""
              }`
            : "",
    contact: formatContact(
        templateData?.contact_name,
        templateData?.contact_poste
    ),
    other: templateData?.other,
    otherInterim: templateData?.other_interim,
});

/**
 * Document API / liste (getMissionTemplates) — même champs affichés que le formulaire.
 */
export const buildSummaryFromApiTemplate = (
    template,
    language,
    riskyPositions
) => ({
    name: getTemplateTitle(template),
    activity: template?.activity,
    qualification: getTemplateQualification(template),
    riskyPosition: formatRiskyPositionSummary(
        template?.conditions,
        language,
        riskyPositions
    ),
    tools: formatTemplateToolsWithProvider(template?.tools, language),
    licences: formatList(template?.licences),
    languages: formatList(template?.language || template?.languages),
    address: formatAddress(template?.location),
    salary: formatSalaryApi(template),
    contact: formatContact(template?.contact?.name, template?.contact?.poste),
    other: template?.other,
    otherInterim: template?.other_interim,
});

export const MissionTemplateSummaryRows = ({ summary }) => {
    const { language } = UseContext();
    if (!summary) return null;
    return (
        <div className={"flex flex-col"}>
            {SUMMARY_ROWS.map(({ key, defaultLabel }) => (
                <Row
                    key={key}
                    label={language.getValue(
                        `missionTemplate.preview.fields.${key}`,
                        defaultLabel
                    )}
                    value={summary[key]}
                />
            ))}
        </div>
    );
};

/**
 * Point d’entrée unique : confirmation (wizard) ou modal (document API).
 * Passez **soit** `wizardData` **soit** `apiTemplate` — pas les deux.
 */
const MissionTemplateSummary = ({ wizardData, apiTemplate }) => {
    const { language } = UseContext();
    const { riskyPositions } = useRiskyPositions();
    const summary =
        wizardData != null
            ? buildSummaryFromWizardData(wizardData, language, riskyPositions)
            : buildSummaryFromApiTemplate(
                  apiTemplate,
                  language,
                  riskyPositions
              );

    return <MissionTemplateSummaryRows summary={summary} />;
};

export default MissionTemplateSummary;
